/**
 * Phase 14a-2 — `?next=` redirect allowlist + resolver.
 *
 * The portal sign-in route (sign-in.tsx + sign-in.verify.tsx) honours a
 * `?next=<encoded URL>` query param so the admin app's route guard can
 * push unauthenticated operators into the portal sign-in then bounce
 * them straight to the admin URL they originally tried to reach.
 *
 * **Open-redirect mitigation is a hard requirement** — without an
 * allowlist, an attacker could deliver a magic-link with
 * `?next=http://evil.com/fake-portal`; the victim authenticates and is
 * redirected to the attacker site that screenshots / phishes the
 * authenticated UI.
 *
 * Allowlist construction:
 *   - VITE_PORTAL_URL host (portal can redirect to itself)
 *   - VITE_ADMIN_URL host (admin redirect — the 14a-2 use case)
 *   - Falsy entries (env vars unset in dev) are skipped — dev still
 *     blocks every absolute URL when both env vars are absent, which is
 *     correct fail-closed default.
 *
 * Relative `next` values (starting with `/`) are ALSO rejected on
 * purpose at v1 — same-origin same-app navigation already happens via
 * the post-sign-in default of `/me`; cross-app navigation is the only
 * legitimate `next` use case at 14a.
 */

function allowedHosts(): ReadonlySet<string> {
  const hosts = new Set<string>();
  for (const raw of [
    import.meta.env['VITE_PORTAL_URL'],
    import.meta.env['VITE_ADMIN_URL'],
    import.meta.env['PUBLIC_PORTAL_URL'],
    import.meta.env['PUBLIC_ADMIN_URL'],
  ]) {
    if (typeof raw !== 'string' || raw.length === 0) continue;
    try {
      hosts.add(new URL(raw).host);
    } catch {
      // Bad env value — ignore. The empty allowlist falls back to "default
      // redirect to /me" which is the safe outcome.
    }
  }
  // Derive `app.` + `admin.` of THIS portal's own registrable domain and add
  // them, so the portal↔admin round-trip (e.g. sign-out → sign-in → back to
  // admin) works with zero env config on staging/prod. Still fail-closed: only
  // the `app.`/`admin.` siblings of the host the user is currently on are
  // allowed — a `?next=` to any other domain is still rejected. Dev tunnels
  // (`admin-dev.<domain>`) keep working via the VITE_* env entries above.
  if (typeof window !== 'undefined') {
    const host = window.location.host.split(':')[0] ?? '';
    const labels = host.split('.');
    if (labels.length >= 2) {
      const base = labels.slice(-2).join('.'); // registrable domain (2-label TLDs)
      hosts.add(`app.${base}`);
      hosts.add(`admin.${base}`);
    }
  }
  return hosts;
}

/**
 * Returns true iff `next` is a parseable absolute URL whose host is on
 * the allowlist. Relative URLs, malformed URLs, and any host outside
 * the allowlist all return false (the caller falls back to `/me`).
 *
 * Exported for unit testing — production callers should prefer
 * `resolveNextRedirect()`.
 */
export function isAllowedNext(next: string | null | undefined): boolean {
  if (typeof next !== 'string' || next.length === 0) return false;
  let url: URL;
  try {
    url = new URL(next);
  } catch {
    return false;
  }
  // Disallow non-http(s) schemes (javascript:, data:, etc).
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  return allowedHosts().has(url.host);
}

/**
 * Returns the `next` URL when allowlisted, otherwise null. Callers
 * use:
 *
 *   const target = resolveNextRedirect(searchParams.get('next'));
 *   if (target) window.location.href = target;
 *   else navigate({ to: '/me' });
 */
export function resolveNextRedirect(
  next: string | null | undefined,
): string | null {
  return isAllowedNext(next) ? (next as string) : null;
}

/**
 * Phase 14a-2 follow-up — persona-aware default post-sign-in destination.
 *
 * When no `?next=` is provided (the common case: operator types portal
 * URL, signs in), the post-sign-in destination depends on which persona
 * the user has:
 *   - Caregiver / Facility User / Homecare Client → /me (portal home;
 *     /me renders their persona panel)
 *   - Operator-only (no customer-facing persona) → admin app via
 *     VITE_ADMIN_URL (operator tooling lives at apps/admin per Phase 14-A)
 *   - Operator-AND-customer (rare; QA / dual-role) → /me; operator
 *     reaches admin via bookmark
 *   - No matching persona → /me (the welcome placeholder explains the
 *     missing surface)
 *
 * Returns a string usable for either same-origin TanStack Router
 * `navigate({ to: dest })` (when dest === '/me') OR cross-origin
 * `window.location.href = dest` (when dest is an absolute URL).
 * Callers branch on `dest.startsWith('http')` to choose the navigation
 * mechanism.
 */
export interface PersonaRoleEntry {
  persona: string;
}

const CUSTOMER_PERSONAS: ReadonlySet<string> = new Set([
  'CAREGIVER',
  'FACILITY_USER',
  'HOMECARE_CLIENT_USER',
]);

/**
 * Derive the admin app URL from the portal's OWN host at runtime — `admin.` +
 * this site's registrable domain (portal on `app.<domain>` → `admin.<domain>`).
 * Zero per-env config; `VITE_ADMIN_URL` overrides it for dev tunnels
 * (`admin-dev.<domain>`). Returns null under SSR or a non-domain host (bare
 * `localhost`), so those cases fall back to the `/me` placeholder as before.
 * Only ever targets the `admin.` sibling of the current registrable domain, so
 * it can't be steered off-domain (not an open-redirect vector).
 */
function deriveAdminUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.host.split(':')[0] ?? '';
  const labels = host.split('.');
  if (labels.length < 2) return null;
  const base = labels.slice(-2).join('.'); // registrable domain (our TLDs are 2-label)
  return `${window.location.protocol}//admin.${base}`;
}

/**
 * Resolve the admin app URL: an explicit env override wins (dev tunnels set
 * `VITE_ADMIN_URL=admin-dev.<domain>`), otherwise derive `admin.` + this
 * portal's own registrable domain at runtime. Returns null only under SSR or
 * a bare non-domain host (localhost) with no env override.
 *
 * This is the single source of truth for "where does operator tooling live?"
 * — both `resolveDefaultPostSignInDestination` (sign-in) and the `/me`
 * operator branch use it, so staging/prod need NO baked admin host (the
 * portal build deliberately ships without one — see `.do/app.*.yaml`).
 *
 * Empty-string env values are treated as unset — `??` alone would keep `''`
 * and skip the derivation, so we length-check before falling through.
 */
export function resolveAdminUrl(): string | null {
  const viteAdmin = import.meta.env['VITE_ADMIN_URL'];
  const publicAdmin = import.meta.env['PUBLIC_ADMIN_URL'];
  return (
    (typeof viteAdmin === 'string' && viteAdmin.length > 0 ? viteAdmin : null) ??
    (typeof publicAdmin === 'string' && publicAdmin.length > 0
      ? publicAdmin
      : null) ??
    deriveAdminUrl()
  );
}

export function resolveDefaultPostSignInDestination(
  roles: readonly PersonaRoleEntry[],
): string {
  const hasCustomerPersona = roles.some((r) =>
    CUSTOMER_PERSONAS.has(r.persona),
  );
  if (hasCustomerPersona) return '/me';
  const isOperator = roles.some((r) => r.persona === 'OPERATOR');
  if (isOperator) {
    // Operator tooling lives at apps/admin (Phase 14-A). Resolve via env
    // override → runtime derivation (see `resolveAdminUrl`); staging/prod need
    // no baked admin host.
    const adminUrl = resolveAdminUrl();
    if (adminUrl) {
      return adminUrl;
    }
    // Still nothing (SSR / localhost with no override) — fall through to /me,
    // which shows the graceful welcome placeholder.
  }
  return '/me';
}
